import type { Writable } from "svelte/store";
import type { SftpConfig } from "./fs";
import { FS } from "./fs";
import { FsDir } from "./fsentry";
import type { HttpProgress } from "./http";
import { mkURL } from "./http";
import { throwIf } from "./util";

// The browser cannot speak SSH. File calls go through /api/sftp. The password,
// private key, and key password are request headers, not query parameters.

class FileSystemSftp extends FS {
  supportsDirRename = true;

  constructor(config: SftpConfig) {
    super(config);
  }

  get sftpConfig(): SftpConfig {
    return this.config as SftpConfig;
  }

  async readDir(dirPath: string, force: boolean, progress: Writable<any>): Promise<FsDir> {
    throwIf(dirPath == "", "empty dir");
    return this.loadDir(dirPath, force, progress, async () => {
      let entries = await sftpList(this.sftpConfig, dirPath);
      return FsDir.fromWire(dirPath, entries);
    });
  }

  async getPublicURL(path: string): Promise<string> {
    // Preview and the download link cannot send the key headers. Fetch through
    // the same-origin proxy and hand the bytes back as a blob URL.
    let blob = await this.downloadFile(path, () => {});
    return URL.createObjectURL(blob);
  }

  async downloadFile(path: string, httpProgress: HttpProgress): Promise<Blob> {
    let url = sftpURL(this.sftpConfig, "/api/sftp/download", "path", path);
    let xhr = await sftpSend("GET", url, this.sftpConfig, undefined, httpProgress, "blob");
    return xhr.response as Blob;
  }

  async uploadFile(path: string, file: Blob, httpProgress: HttpProgress): Promise<void> {
    let url = sftpURL(this.sftpConfig, "/api/sftp/upload", "path", path);
    await sftpSend("PUT", url, this.sftpConfig, file, httpProgress);
  }

  async rename(path: string, newPath: string): Promise<void> {
    throwIf(path == newPath, "path must be != newPath");
    let url = sftpURL(this.sftpConfig, "/api/sftp/rename", "path", path, "newPath", newPath);
    await sftpCall(this.sftpConfig, url, "POST");
  }

  async createDirectory(path: string): Promise<void> {
    let url = sftpURL(this.sftpConfig, "/api/sftp/mkdir", "path", path);
    await sftpCall(this.sftpConfig, url, "POST");
  }

  async deleteFile(path: string): Promise<void> {
    let url = sftpURL(this.sftpConfig, "/api/sftp/delete", "path", path);
    await sftpCall(this.sftpConfig, url, "POST");
  }
}

export async function testSftpConnection(config: SftpConfig): Promise<{ hostKey: string }> {
  let js = await sftpCall(config, sftpURL(config, "/api/sftp/test"));
  if (!js.hostKey) {
    throw new Error("sftp did not return a host key");
  }
  return { hostKey: js.hostKey };
}

async function sftpList(config: SftpConfig, dir: string): Promise<any[][]> {
  let js = await sftpCall(config, sftpURL(config, "/api/sftp/list", "dir", dir));
  if (!Array.isArray(js)) {
    throw new Error("sftp list failed");
  }
  return js;
}

function sftpURL(config: SftpConfig, path: string, ...args: string[]): string {
  return mkURL(path, "host", config.host, "port", config.port || "22", ...args);
}

function encodeSecret(s: string): string {
  let bytes = new TextEncoder().encode(s);
  let bin = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(bin);
}

function sftpHeaders(config: SftpConfig): Record<string, string> {
  let headers: Record<string, string> = {
    "X-Sftp-User": encodeSecret(config.user),
  };
  if (config.password) {
    headers["X-Sftp-Password"] = encodeSecret(config.password);
  }
  if (config.key) {
    headers["X-Sftp-Key"] = encodeSecret(config.key);
  }
  if (config.keyPass) {
    headers["X-Sftp-Key-Pass"] = encodeSecret(config.keyPass);
  }
  if (config.hostKey) {
    headers["X-Sftp-Host-Key"] = config.hostKey;
  }
  return headers;
}

async function sftpCall(config: SftpConfig, url: string, method = "GET"): Promise<any> {
  let rsp = await fetch(url, { method, headers: sftpHeaders(config) });
  let text = await rsp.text();
  let js: any = {};
  if (text) {
    try {
      js = JSON.parse(text);
    } catch {
      js = {};
    }
  }
  if (!rsp.ok) {
    throw new Error(js.error || `sftp ${rsp.status}`);
  }
  return js;
}

function sftpSend(
  method: string,
  url: string,
  config: SftpConfig,
  data: Blob | undefined,
  onProgress: HttpProgress | undefined,
  responseType?: XMLHttpRequestResponseType,
): Promise<XMLHttpRequest> {
  let headers = sftpHeaders(config);
  return new Promise((resolve, reject) => {
    let xhr = new XMLHttpRequest();
    xhr.open(method, url);
    if (responseType) {
      xhr.responseType = responseType;
    }
    for (let [k, v] of Object.entries(headers)) {
      xhr.setRequestHeader(k, v);
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr);
        return;
      }
      sftpErrText(xhr).then(
        (msg) => reject(new Error(msg)),
        () => reject(new Error(`sftp ${xhr.status}`)),
      );
    };
    xhr.onerror = () => {
      reject(new Error("sftp request failed"));
    };
    if (onProgress) {
      let started = Date.now();
      let report = (ev: ProgressEvent) => {
        let total = ev.lengthComputable ? ev.total : 0;
        onProgress(ev.loaded, total, Date.now() - started);
      };
      if (method == "PUT") {
        xhr.upload.onprogress = report;
      } else {
        xhr.onprogress = report;
      }
    }
    if (data) {
      xhr.send(data);
    } else {
      xhr.send();
    }
  });
}

async function sftpErrText(xhr: XMLHttpRequest): Promise<string> {
  let text = "";
  if (xhr.responseType == "blob" && xhr.response instanceof Blob) {
    text = await xhr.response.text();
  } else if (xhr.responseType == "" || xhr.responseType == "text") {
    text = xhr.responseText || "";
  }
  if (text) {
    try {
      let js = JSON.parse(text);
      if (js.error) {
        return String(js.error);
      }
    } catch {
      // keep the status
    }
  }
  return `sftp ${xhr.status}`;
}

export function makeFileSystemSftp(config: SftpConfig): FileSystemSftp {
  return new FileSystemSftp(config);
}
